import { provide, computed, inject } from 'vue'
import type { Ref, ComputedRef } from 'vue'
import get from 'lodash/get'
import useVuelidate from '@vuelidate/core'
import type { ValidationArgs, Validation } from '@vuelidate/core'
import { usePage } from '@inertiajs/vue3'

type ValidationRules = ValidationArgs
type FormData = Record<string, unknown>
type ErrorData = Record<string, string | string[]> | null

interface ValidationResult {
  validator: Ref<Validation>
  isFormValid: ComputedRef<boolean>
  validateForm: () => boolean
  resetForm: () => void
}

export const useValidation = (
  form: Ref<FormData>,
  rules: ValidationRules,
  errors: Ref<ErrorData> | null = null,
  namespace: string = 'validator',
): ValidationResult => {
  const validator = useVuelidate(rules, form)
  const isFormValid = computed(() => !validator.value.$error)

  const validateForm = (): boolean => {
    // trigger validation
    validator.value.$touch()
    return isFormValid.value
  }

  const resetForm = (): void => {
    validator.value.$reset()
  }

  // provide validation to all fields on this page
  const validatorName = namespace
  provide(validatorName, validator)
  provide('errors', errors)

  return {
    validator,
    isFormValid,
    validateForm,
    resetForm,
  }
}

interface FieldValidationProps {
  name: string
  error?: string | string[] | Record<string, unknown> | null
  validator?: string
}

interface FieldValidationResult {
  v$: ComputedRef<unknown>
  displayedError: ComputedRef<string | false>
  validationErrors: ComputedRef<string[]>
  serverError: ComputedRef<string | string[] | null>
}

export const useFieldValidation = (props: FieldValidationProps): FieldValidationResult => {
  // compute validation errors
  const validatorName = props.validator || 'validator'
  const validatorInstance = inject<Ref<Validation>>(validatorName)
  const providedErrors = inject<Ref<ErrorData>>('errors')

  const v$ = computed(() => {
    return validatorInstance ? get(validatorInstance.value, props.name, null) : null
  })

  const validationErrors = computed((): string[] => {
    if (v$.value === undefined || v$.value === null) return []

    const field = v$.value as unknown as {
      $dirty?: boolean
      $errors?: Array<{ $message: string }>
    }
    return field && field.$dirty ? field.$errors?.map((err) => err.$message) || [] : []
  })

  // format error prop as it can be array or string or object
  const firstErrorProp = computed((): string | string[] | null => {
    if (typeof props.error === 'object' && props.error !== null && !Array.isArray(props.error)) {
      return get(props.error, props.name, null) as string | string[] | null
    } else {
      return props.error as string | string[] | null
    }
  })

  // errors provided directly in the validator
  const secondErrorProp = computed((): string | string[] | null =>
    providedErrors ? get(providedErrors.value, props.name, null) : null,
  )

  // compute error to display (validation error from vuelidate, server error, or prop or nothing)
  const serverError = computed((): string | string[] | null => {
    const pageProps = usePage()?.props?.value as unknown as {
      errors?: Record<string, string | string[]>
      error?: Record<string, string | string[]>
    }
    return get(pageProps?.errors, props.name, null) || get(pageProps?.error, props.name, null)
  })

  const displayedError = computed((): string | false => {
    if (firstErrorProp.value) {
      return Array.isArray(firstErrorProp.value) ? firstErrorProp.value[0] : firstErrorProp.value
    } else if (secondErrorProp.value) {
      return Array.isArray(secondErrorProp.value) ? secondErrorProp.value[0] : secondErrorProp.value
    } else if (validationErrors.value.length > 0) {
      return validationErrors.value[0]
    } else if (serverError.value) {
      return Array.isArray(serverError.value) ? serverError.value[0] : serverError.value
    } else {
      return false
    }
  })

  return {
    v$,
    displayedError,
    validationErrors,
    serverError,
  }
}

export type { ValidationResult, FieldValidationProps, FieldValidationResult, FormData, ErrorData }
